import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getSubgroupParts, getModelDetail } from "../lib/api.js";
import type {
  ModelDetail,
  PartListItem,
  SubgroupPartsResponse,
} from "../lib/types.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { Badge } from "../ui/Badge.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { PriceDisplay } from "../ui/PriceDisplay.js";
import { StockBadge } from "../ui/StockBadge.js";
import {
  ImageOffIcon,
  ZoomInIcon,
  ZoomOutIcon,
  SearchIcon,
  PackageIcon,
  ChevronRightIcon,
  CartIcon,
} from "../lib/icons.js";
import { useQuoteCart } from "../contexts/QuoteCartContext.js";

/**
 * Obrazovka D — explodovaný výkres + seznam dílů.
 *
 * Core UX:
 *   - Levý sloupec: image + SVG overlay hotspotů
 *   - Pravý sloupec: list dílů, virtualizovaný klik
 *   - Oboustranná interakce: hover na list → glow hotspot; klik na hotspot → scroll + highlight
 *   - Zoom (0.5×–3×) + pan
 */
export function DrawingPage(): JSX.Element {
  const { modelId, subgroupId } = useParams<{ modelId: string; subgroupId: string }>();
  const [model, setModel] = useState<ModelDetail | null>(null);
  const [data, setData] = useState<SubgroupPartsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePartId, setActivePartId] = useState<string | null>(null);
  const [hoveredPartId, setHoveredPartId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!modelId || !subgroupId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getSubgroupParts(subgroupId, modelId), getModelDetail(modelId)])
      .then(([d, m]) => {
        if (cancelled) return;
        setData(d);
        setModel(m);
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [modelId, subgroupId]);

  const filteredParts = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return data.parts;
    return data.parts.filter(
      (p) =>
        p.article.articleNumber.toLowerCase().includes(s) ||
        p.article.name?.toLowerCase().includes(s) ||
        String(p.lineNumber ?? "").includes(s),
    );
  }, [data, search]);

  if (loading) return <LoadingBlock label="Načítám výkres…" />;
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!data || !model)
    return (
      <EmptyState title="Výkres nenalezen" description="Zkontrolujte URL." />
    );

  const modelLabel = [model.description?.trim(), model.year].filter(Boolean).join(" ");
  const subgroupLabel = data.subgroup.description;
  const subgroupImageUrl =
    data.subgroup.explosionImageUrl ?? data.subgroup.explosionImageUrlHigh ?? null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
      <div className="mb-5">
        <Breadcrumbs
          items={[
            { label: model.brand.shortName, to: "/search/model" },
            { label: model.description ?? "Model", to: `/model/${model.id}` },
            { label: data.subgroup.description },
          ]}
        />
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge tone="orange">{data.subgroup.code}</Badge>
            <Badge tone="neutral">{data.parts.length} dílů</Badge>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
            {data.subgroup.description}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_480px] gap-6">
        <DrawingViewer
          imageUrl={data.subgroup.explosionImageUrlHigh ?? data.subgroup.explosionImageUrl}
          parts={data.parts}
          activePartId={activePartId}
          hoveredPartId={hoveredPartId}
          onPartClick={(id) => setActivePartId(id)}
          onPartHover={setHoveredPartId}
        />
        <PartsSidebar
          parts={filteredParts}
          total={data.parts.length}
          activePartId={activePartId}
          hoveredPartId={hoveredPartId}
          search={search}
          onSearchChange={setSearch}
          onPartClick={(id) => setActivePartId(id)}
          onPartHover={setHoveredPartId}
          context={{
            modelId: data.modelId,
            modelLabel,
            subgroupId: data.subgroup.id,
            subgroupLabel,
            subgroupImageUrl,
          }}
        />
      </div>
    </div>
  );
}

/* ──────────────────────────── Viewer ──────────────────────────── */

function DrawingViewer({
  imageUrl,
  parts,
  activePartId,
  hoveredPartId,
  onPartClick,
  onPartHover,
}: {
  imageUrl: string | null;
  parts: PartListItem[];
  activePartId: string | null;
  hoveredPartId: string | null;
  onPartClick: (id: string) => void;
  onPartHover: (id: string | null) => void;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);

  // Mapa: hotspotId -> part (pro rychlé dohledání při kliku)
  const hotspotToPart = useMemo(() => {
    const m = new Map<string, PartListItem>();
    for (const p of parts) {
      for (const h of p.hotspots) m.set(h.id, p);
    }
    return m;
  }, [parts]);

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>): void {
    const img = e.currentTarget;
    setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
  }

  // Zkus odvodit rozsah coord — BFF používá relativní 0..1 nebo pixely,
  // my si z hotspotů spočteme max hodnotu a podle toho škálujeme overlay.
  const coordMax = useMemo(() => {
    let max = 0;
    for (const p of parts) {
      for (const h of p.hotspots) {
        if (h.endX != null && h.endX > max) max = h.endX;
        if (h.endY != null && h.endY > max) max = h.endY;
        if (h.startX != null && h.startX > max) max = h.startX;
        if (h.startY != null && h.startY > max) max = h.startY;
      }
    }
    // Pokud max <= 1, souřadnice jsou 0..1; pokud > 100, jsou v pixelech.
    return max;
  }, [parts]);

  const viewBoxWidth = coordMax <= 1.001 ? 1 : coordMax;
  const viewBoxHeight = viewBoxWidth;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100">
        <div className="text-xs text-zinc-500">
          Najeďte myší nebo klikněte na díl pro zvýraznění
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            className="w-8 h-8 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
            aria-label="Oddálit"
          >
            <ZoomOutIcon size={18} />
          </button>
          <div className="text-xs text-zinc-500 tabular-nums w-12 text-center">
            {Math.round(zoom * 100)}%
          </div>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
            className="w-8 h-8 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
            aria-label="Přiblížit"
          >
            <ZoomInIcon size={18} />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="ml-1 px-2 h-8 rounded-md hover:bg-zinc-100 text-xs text-zinc-600 font-medium"
          >
            Reset
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="relative bg-zinc-50 overflow-auto"
        style={{ maxHeight: "min(75vh, 820px)" }}
      >
        {imageUrl ? (
          <div
            className="relative inline-block origin-top-left"
            style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}
          >
            <img
              src={imageUrl}
              alt="Explodovaný výkres"
              onLoad={handleImageLoad}
              className="block select-none"
              draggable={false}
              style={{ maxWidth: "1200px" }}
            />
            {imgSize && (
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                preserveAspectRatio="none"
              >
                {parts.map((p) =>
                  p.hotspots.map((h) => {
                    if (
                      h.startX == null ||
                      h.startY == null ||
                      h.endX == null ||
                      h.endY == null
                    )
                      return null;
                    // BFF posílá souřadnice v matematické konvenci (Y = 0 dole).
                    // Pro SVG/CSS je potřeba Y-flip: y_css = 1 - endY (u 0..1 framu).
                    // Pro pixelové souřadnice (coordMax > 1) stejná logika: y_css = coordMax - endY.
                    const frameMax = coordMax <= 1.001 ? 1 : coordMax;
                    const x = Math.min(h.startX, h.endX);
                    const w = Math.abs(h.endX - h.startX);
                    const hh = Math.abs(h.endY - h.startY);
                    // Y-flip
                    const y = frameMax - Math.max(h.startY, h.endY);
                    const isActive = activePartId === p.id;
                    const isHovered = hoveredPartId === p.id;
                    return (
                      <g
                        key={h.id}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => onPartHover(p.id)}
                        onMouseLeave={() => onPartHover(null)}
                        onClick={() => onPartClick(p.id)}
                      >
                        <rect
                          x={x}
                          y={y}
                          width={w}
                          height={hh}
                          fill={
                            isActive
                              ? "rgba(255, 102, 0, 0.35)"
                              : isHovered
                                ? "rgba(255, 102, 0, 0.2)"
                                : "rgba(255, 102, 0, 0)"
                          }
                          stroke={
                            isActive || isHovered ? "rgba(255, 102, 0, 0.9)" : "transparent"
                          }
                          strokeWidth={
                            coordMax <= 1.001 ? 0.003 : Math.max(1, coordMax / 500)
                          }
                          vectorEffect="non-scaling-stroke"
                          className="transition-all duration-100"
                        />
                        {/* Číselné kolečko renderujeme mimo stretched SVG (viz overlay níže) */}
                      </g>
                    );
                  }),
                )}
              </svg>
            )}
            {/* HTML overlay pro labely pozic — kolečka zachovají kulatý tvar
                i když image má non-1:1 aspect (SVG overlay výše je stretch).
                Pokud má díl víc hotspotů (typicky opakovaná schránka, šroub, hadice),
                zvýrazní se VŠECHNY výskyty — mechanik pak vidí kompletní množinu pozic. */}
            {imgSize && (
              <div className="absolute inset-0 pointer-events-none">
                {parts.flatMap((p) => {
                  const isActive = activePartId === p.id;
                  const isHovered = hoveredPartId === p.id;
                  if ((!isActive && !isHovered) || p.lineNumber == null) return [];
                  const frameMax = coordMax <= 1.001 ? 1 : coordMax;
                  return p.hotspots
                    .map((h, idx) => {
                      if (
                        h.startX == null ||
                        h.startY == null ||
                        h.endX == null ||
                        h.endY == null
                      ) {
                        return null;
                      }
                      const cx = ((h.startX + h.endX) / 2) / frameMax;
                      const cyMath = (h.startY + h.endY) / 2;
                      const cy = (frameMax - cyMath) / frameMax; // Y-flip
                      return (
                        <div
                          key={`lbl-${p.id}-${idx}`}
                          className="absolute flex items-center justify-center rounded-full bg-ktm-orange text-white text-[11px] font-semibold shadow-lg ring-2 ring-white"
                          style={{
                            left: `${cx * 100}%`,
                            top: `${cy * 100}%`,
                            width: 22,
                            height: 22,
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          {p.lineNumber}
                        </div>
                      );
                    })
                    .filter((x): x is JSX.Element => x !== null);
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-96 text-zinc-300">
            <ImageOffIcon size={40} />
          </div>
        )}
      </div>
      <div className="px-4 py-2 border-t border-zinc-100 text-[11px] text-zinc-400 flex items-center justify-between">
        <span>{parts.length} pozic</span>
        <span>{hotspotToPart.size} hotspotů</span>
      </div>
    </div>
  );
}

/* ──────────────────────────── Sidebar ──────────────────────────── */

interface DrawingContext {
  modelId: string;
  modelLabel: string;
  subgroupId: string;
  subgroupLabel: string;
  subgroupImageUrl: string | null;
}

function PartsSidebar({
  parts,
  total,
  activePartId,
  hoveredPartId,
  search,
  onSearchChange,
  onPartClick,
  onPartHover,
  context,
}: {
  parts: PartListItem[];
  total: number;
  activePartId: string | null;
  hoveredPartId: string | null;
  search: string;
  onSearchChange: (v: string) => void;
  onPartClick: (id: string) => void;
  onPartHover: (id: string | null) => void;
  context: DrawingContext;
}): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll do view při aktivaci z klikem na hotspotu
  useEffect(() => {
    if (!activePartId || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-part-id="${activePartId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activePartId]);

  return (
    <aside className="bg-white rounded-2xl border border-zinc-200 shadow-card flex flex-col overflow-hidden">
      <div className="p-4 border-b border-zinc-100">
        <div className="relative">
          <SearchIcon
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <input
            type="text"
            placeholder="Hledat v dílech…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm placeholder:text-zinc-400 focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none"
          />
        </div>
        <div className="mt-2 text-xs text-zinc-500">
          Zobrazeno {parts.length} z {total}
        </div>
      </div>

      <div
        ref={listRef}
        className="flex-1 overflow-auto divide-y divide-zinc-100"
        style={{ maxHeight: "min(75vh, 820px)" }}
      >
        {parts.length === 0 ? (
          <EmptyState
            icon={<PackageIcon size={24} />}
            title="Žádné díly"
            description={search ? "Upravte dotaz." : "V této podskupině nejsou díly."}
          />
        ) : (
          parts.map((p) => (
            <PartRow
              key={p.id}
              part={p}
              isActive={activePartId === p.id}
              isHovered={hoveredPartId === p.id}
              onClick={() => onPartClick(p.id)}
              onHover={onPartHover}
              context={context}
            />
          ))
        )}
      </div>
    </aside>
  );
}

/**
 * Fyzický sklad Vlašim — nositel pravdy (warehouse_stock, kind='own').
 * `qtySellable = qtyAvailable + qtyInbound` (co lze prodat).
 */
function PhysicalStockBadge({
  physicalStock,
}: {
  physicalStock: PartListItem["physicalStock"];
}): JSX.Element {
  if (!physicalStock) {
    return (
      <div className="flex flex-col gap-0.5 text-xs">
        <span className="text-zinc-500 text-[11px] uppercase tracking-wide">Vlašim</span>
        <Badge tone="neutral" className="self-start">
          neznámo
        </Badge>
      </div>
    );
  }
  const sellable = physicalStock.qtySellable;
  const tone: "green" | "amber" | "red" = sellable > 0 ? "green" : "red";
  const label =
    sellable > 0
      ? physicalStock.qtyInbound > 0 && physicalStock.qtyAvailable === 0
        ? `${physicalStock.qtyInbound} ks v příjmu`
        : `${sellable} ks skladem`
      : "není skladem";
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span className="text-zinc-500 text-[11px] uppercase tracking-wide">Vlašim</span>
      <Badge tone={tone} className="self-start">
        {label}
      </Badge>
    </div>
  );
}

function PartRow({
  part,
  isActive,
  isHovered,
  onClick,
  onHover,
  context,
}: {
  part: PartListItem;
  isActive: boolean;
  isHovered: boolean;
  onClick: () => void;
  onHover: (id: string | null) => void;
  context: DrawingContext;
}): JSX.Element {
  const { addItem } = useQuoteCart();
  const canAdd = !!part.price;

  function handleAdd(e: React.MouseEvent): void {
    e.stopPropagation();
    if (!part.price) return;
    addItem({
      articleNumber: part.article.articleNumber,
      articleName: part.article.name,
      unitPriceNetCzk: part.price.net.czk,
      vatRate: part.price.vatRate,
      modelId: context.modelId,
      modelLabel: context.modelLabel,
      subgroupId: context.subgroupId,
      subgroupLabel: context.subgroupLabel,
      subgroupImageUrl: context.subgroupImageUrl,
      lineNumber: part.lineNumber,
      articleImageUrl: part.article.imageUrl,
    });
  }

  return (
    <div
      data-part-id={part.id}
      onMouseEnter={() => onHover(part.id)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
      className={`relative p-4 cursor-pointer transition-colors ${
        isActive
          ? "bg-ktm-orange-50"
          : isHovered
            ? "bg-zinc-50"
            : "hover:bg-zinc-50"
      }`}
    >
      {isActive && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-ktm-orange" />
      )}
      <div className="flex items-start gap-3">
        <PositionPill number={part.lineNumber} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 leading-snug line-clamp-2">
                {part.article.name ?? "Bez názvu"}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500 flex-wrap">
                <span className="font-mono">{part.article.articleNumber}</span>
                {part.quantity > 1 && (
                  <span className="text-zinc-400">× {part.quantity}</span>
                )}
                {part.isNew && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide"
                    title="Nový díl v tomto modelu"
                  >
                    ● Nový
                  </span>
                )}
                {part.articleStatus === "80" && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-semibold uppercase tracking-wide"
                    title="Zrušený / discontinued díl (status 80)"
                  >
                    ✕ Zrušen
                  </span>
                )}
                {part.replacedByArticleNumber && part.articleStatus !== "80" && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold uppercase tracking-wide"
                    title={`Nahrazen novějším dílem ${part.replacedByArticleNumber}`}
                  >
                    ↻ Nahrazen
                  </span>
                )}
              </div>
            </div>
            <Link
              to={`/part/${encodeURIComponent(part.article.articleNumber)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-zinc-400 hover:text-ktm-orange transition-colors p-1 -m-1"
              title="Otevřít detail"
            >
              <ChevronRightIcon size={16} />
            </Link>
          </div>
          <div className="mt-3 flex items-center gap-4">
            {part.price && (
              <div>
                <PriceDisplay price={part.price} size="sm" />
              </div>
            )}
            <div className="flex items-center gap-3 text-xs ml-auto">
              <PhysicalStockBadge physicalStock={part.physicalStock} />
              <StockBadge label="Výrobce" stock={part.stock.manufacturer} />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              title={canAdd ? "Přidat do nabídky" : "Nejprve nastavit cenu"}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium bg-zinc-900 text-white hover:bg-ktm-orange transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-zinc-900"
            >
              <CartIcon size={13} />
              Přidat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PositionPill({ number }: { number: number | null }): JSX.Element {
  return (
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
        number != null
          ? "bg-zinc-900 text-white"
          : "bg-zinc-100 text-zinc-400"
      }`}
    >
      {number ?? "—"}
    </div>
  );
}
